import manifestRaw from './cfoDrilldownManifest.json?raw'

const trimStr = (value: unknown): string => String(value ?? '').trim()

export type CfoDrilldownViewManifest = {
  id: string
  table: string
  label: string
  required_columns: string[]
  source_artifacts?: string[]
}

export type CfoDrilldownRecipeManifest = {
  id: string
  label: string
  description: string
  required_views: string[]
  default_view_kind: 'bar' | 'table' | string
  grain: string
  supports_custom_sql_fallback?: boolean
}

export type CfoDrilldownMetricBinding = {
  metrics: string[]
  source_components?: string[]
  recipe_id: string
}

export type CfoDrilldownManifest = {
  version: string
  default_render_policy?: {
    preferred?: 'visual' | 'table' | string
    dense_row_threshold?: number
    dense_column_threshold?: number
    fallback?: 'table' | 'visual' | string
  }
  views: CfoDrilldownViewManifest[]
  recipes: CfoDrilldownRecipeManifest[]
  metric_bindings: CfoDrilldownMetricBinding[]
  fallback_policy?: {
    custom_sql_allowed?: boolean
    custom_sql_scope?: string
    instructions?: string
  }
}

export const CFO_DRILLDOWN_MANIFEST = JSON.parse(manifestRaw) as CfoDrilldownManifest

const SUPPORTED_VIEW_KINDS = new Set(['bar', 'table'])

export const validateCfoDrilldownManifest = (manifest: CfoDrilldownManifest): string[] => {
  const errors: string[] = []
  if (!trimStr(manifest.version)) errors.push('Manifest version is required.')

  const viewIds = new Set<string>()
  for (const view of manifest.views || []) {
    const id = trimStr(view.id)
    if (!id) errors.push('Every manifest view needs an id.')
    if (id && viewIds.has(id)) errors.push(`Duplicate view id "${id}".`)
    if (id) viewIds.add(id)
    if (!trimStr(view.table)) errors.push(`View "${id || '(missing id)'}" needs a table name.`)
    if (!trimStr(view.label)) errors.push(`View "${id || '(missing id)'}" needs a label.`)
    if (!Array.isArray(view.required_columns)) errors.push(`View "${id || '(missing id)'}" needs required_columns.`)
  }

  const recipeIds = new Set<string>()
  for (const recipe of manifest.recipes || []) {
    const id = trimStr(recipe.id)
    if (!id) errors.push('Every manifest recipe needs an id.')
    if (id && recipeIds.has(id)) errors.push(`Duplicate recipe id "${id}".`)
    if (id) recipeIds.add(id)
    if (!trimStr(recipe.label)) errors.push(`Recipe "${id || '(missing id)'}" needs a label.`)
    if (!SUPPORTED_VIEW_KINDS.has(trimStr(recipe.default_view_kind))) {
      errors.push(`Recipe "${id || '(missing id)'}" uses unsupported default_view_kind "${recipe.default_view_kind}".`)
    }
    for (const viewId of recipe.required_views || []) {
      if (!viewIds.has(viewId)) errors.push(`Recipe "${id}" references missing view "${viewId}".`)
    }
  }

  for (const binding of manifest.metric_bindings || []) {
    if (!Array.isArray(binding.metrics) || !binding.metrics.some((metric) => trimStr(metric))) {
      errors.push(`Binding for recipe "${trimStr(binding.recipe_id) || '(missing recipe)'}" needs at least one metric alias.`)
    }
    if (!recipeIds.has(trimStr(binding.recipe_id))) {
      errors.push(`Metric binding references missing recipe "${trimStr(binding.recipe_id)}".`)
    }
  }

  if (manifest.fallback_policy?.custom_sql_allowed && manifest.fallback_policy.custom_sql_scope !== 'manifest_views_only') {
    errors.push('Fallback SQL must be scoped to manifest_views_only.')
  }

  return errors
}

export const assertValidCfoDrilldownManifest = (manifest: CfoDrilldownManifest = CFO_DRILLDOWN_MANIFEST): void => {
  const errors = validateCfoDrilldownManifest(manifest)
  if (errors.length) {
    throw new Error(`Invalid CFO drilldown manifest:\n${errors.join('\n')}`)
  }
}

assertValidCfoDrilldownManifest(CFO_DRILLDOWN_MANIFEST)

export const CFO_DRILLDOWN_VIEWS_BY_ID = new Map(CFO_DRILLDOWN_MANIFEST.views.map((view) => [view.id, view]))
export const CFO_DRILLDOWN_RECIPES_BY_ID = new Map(CFO_DRILLDOWN_MANIFEST.recipes.map((recipe) => [recipe.id, recipe]))

const METRIC_ALIASES: Record<string, string> = {
  profit: 'net_income',
  profits: 'net_income',
  income: 'net_income',
  earnings: 'net_income',
  sales: 'revenue',
  topline: 'revenue',
  cogs: 'cost_of_sales',
  cost_of_goods_sold: 'cost_of_sales',
  cost_of_goods: 'cost_of_sales',
  cost: 'cost_of_sales',
  costs: 'cost_of_sales',
  opex: 'operating_expenses',
  expenses: 'operating_expenses',
  ar: 'accounts_receivable',
  dso: 'days_sales_outstanding',
}

export const normalizeCfoDrilldownMetricId = (value: unknown): string => {
  const clean = trimStr(value).toLowerCase()
  return METRIC_ALIASES[clean] || clean
}

export const findCfoDrilldownRecipeForMetric = (
  metricId: unknown,
  sourceComponent?: unknown,
): CfoDrilldownRecipeManifest | null => {
  const metric = normalizeCfoDrilldownMetricId(metricId)
  const source = trimStr(sourceComponent).toLowerCase()
  if (!metric) return null
  for (const binding of CFO_DRILLDOWN_MANIFEST.metric_bindings) {
    const metrics = new Set((binding.metrics || []).map(normalizeCfoDrilldownMetricId))
    if (!metrics.has(metric)) continue
    const sources = (binding.source_components || []).map((item) => trimStr(item).toLowerCase()).filter(Boolean)
    if (source && sources.length && !sources.includes(source)) continue
    return CFO_DRILLDOWN_RECIPES_BY_ID.get(binding.recipe_id) || null
  }
  return null
}

export const requiredCfoDrilldownTablesForRecipe = (recipe: CfoDrilldownRecipeManifest): string[] =>
  (recipe.required_views || [])
    .map((viewId) => CFO_DRILLDOWN_VIEWS_BY_ID.get(viewId)?.table || '')
    .filter(Boolean)
